import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, 
  Crown, 
  Gift, 
  Zap, 
  Users, 
  TrendingUp,
  ExternalLink,
  Play,
  Award,
  Sparkles,
  ArrowRight,
  Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const Instagram = () => {
  const navigate = useNavigate();
  const [topPlayers, setTopPlayers] = useState([]);
  const [currentCompetition, setCurrentCompetition] = useState(null);
  const [topPrize, setTopPrize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPlayers: 0,
    totalGames: 0,
    activePlayers: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch top 3 players
      const leaderboardResponse = await fetch(`${BACKEND_URL}/api/admin/leaderboard`);
      const leaderboardData = await leaderboardResponse.json();
      setTopPlayers((leaderboardData.leaderboard || []).slice(0, 3));

      // Fetch current competition
      const compResponse = await fetch(`${BACKEND_URL}/api/competition/current`);
      const compData = await compResponse.json();
      setCurrentCompetition(compData.competition);

      // Fetch top prize
      const prizeResponse = await fetch(`${BACKEND_URL}/api/admin/prizes`);
      const prizeData = await prizeResponse.json();
      const firstPrize = prizeData.prizes?.find(p => p.position === 1);
      setTopPrize(firstPrize);

      // Calculate stats
      const uniquePlayers = new Set(leaderboardData.leaderboard?.map(entry => entry.player_id)).size;
      setStats({
        totalPlayers: uniquePlayers,
        totalGames: leaderboardData.leaderboard?.length || 0,
        activePlayers: uniquePlayers
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCTA = () => {
    const user = localStorage.getItem('fokatmain_user');
    if (user) {
      navigate('/play-games');
    } else {
      navigate('/signup');
    }
  };

  const getMedalIcon = (rank) => {
    switch(rank) {
      case 1: return <Crown className="w-8 h-8 text-yellow-400" />;
      case 2: return <Award className="w-7 h-7 text-gray-400" />;
      case 3: return <Award className="w-6 h-6 text-amber-600" />;
      default: return <Trophy className="w-5 h-5 text-gray-500" />;
    }
  };

  const getRankBgColor = (rank) => {
    switch(rank) {
      case 1: return 'bg-gradient-to-r from-yellow-500 to-yellow-600';
      case 2: return 'bg-gradient-to-r from-gray-400 to-gray-500';
      case 3: return 'bg-gradient-to-r from-amber-600 to-amber-700';
      default: return 'bg-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Hero Section with Announcement */}
      <div className="relative overflow-hidden">
        {/* Animated Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>

        <div className="relative container mx-auto px-4 pt-16 pb-12">
          {/* Announcement Banner */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/50 rounded-full px-6 py-2 mb-6">
              <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
              <span className="text-yellow-400 font-semibold text-sm sm:text-base">LIVE COMPETITION</span>
              <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
              🎉 BIG ANNOUNCEMENT 🎉
              <br />
              <span className="text-yellow-400 drop-shadow-[0_0_30px_rgba(250,204,21,0.5)]">
                SUNDAY SPECIAL!
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-white font-bold mb-6 animate-pulse">
              Play at 9 PM • Winner Declared at 10 PM! 🏆
            </p>
          </div>

          {/* Competition Details Card */}
          <Card className="bg-black/40 backdrop-blur-xl border-yellow-500/30 max-w-4xl mx-auto mb-8">
            <CardHeader className="text-center border-b border-yellow-500/20">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Trophy className="w-8 h-8 text-yellow-400" />
                <CardTitle className="text-2xl sm:text-3xl text-yellow-400 font-black">
                  SCORE-BASED COMPETITION
                </CardTitle>
                <Trophy className="w-8 h-8 text-yellow-400" />
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-white">
                    <span className="text-green-400 text-xl">✅</span>
                    <span className="text-base">All players who participated</span>
                  </div>
                  <div className="flex items-start gap-3 text-white">
                    <span className="text-green-400 text-xl">✅</span>
                    <span className="text-base">Highest score wins</span>
                  </div>
                  <div className="flex items-start gap-3 text-white">
                    <span className="text-green-400 text-xl">✅</span>
                    <span className="text-base">Game opens: Sunday, 9 PM</span>
                  </div>
                  <div className="flex items-start gap-3 text-white">
                    <span className="text-green-400 text-xl">✅</span>
                    <span className="text-base">Winner announced: Sunday, 10 PM (same day)</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-white">
                    <span className="text-green-400 text-xl">✅</span>
                    <span className="text-base">Live leaderboard on website</span>
                  </div>
                  <div className="flex items-start gap-3 text-white">
                    <span className="text-green-400 text-xl">✅</span>
                    <span className="text-base">Fair & Transparent results</span>
                  </div>
                  <div className="flex items-start gap-3 text-white">
                    <span className="text-green-400 text-xl">✅</span>
                    <span className="text-base">Real prizes for real winners</span>
                  </div>
                </div>
              </div>

              {/* Prize Section */}
              <div className="mt-8 p-6 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Gift className="w-8 h-8 text-yellow-400" />
                  <h3 className="text-2xl font-black text-yellow-400">GRAND PRIZE</h3>
                </div>
                <p className="text-center text-white text-xl font-bold">
                  Mobile Phone
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Current Leaderboard Preview */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">
                📊 CURRENT LEADERBOARD
              </h2>
              <p className="text-gray-400 text-base">Top 3 Players Right Now!</p>
            </div>

            {loading ? (
              <div className="text-center text-gray-400 py-12">
                <div className="animate-spin w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-4">Loading leaderboard...</p>
              </div>
            ) : topPlayers.length > 0 ? (
              <div className="space-y-4">
                {topPlayers.map((player, index) => {
                  const rank = index + 1;
                  return (
                    <Card 
                      key={player.player_id || index}
                      className={`${getRankBgColor(rank)} border-0 transform hover:scale-105 transition-all duration-300`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-12 h-12 bg-black/30 rounded-full">
                              {getMedalIcon(rank)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-2xl font-black text-white">#{rank}</span>
                                <span className="text-lg font-bold text-white">
                                  {player.player_name || player.player_email || 'Anonymous'}
                                </span>
                              </div>
                              <p className="text-sm text-white/80">
                                {player.game_name || 'Multiple Games'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-black text-white">
                              {player.high_score || player.score || 0}
                            </p>
                            <p className="text-sm text-white/80">points</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="bg-black/40 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Trophy className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No players yet. Be the first to compete!</p>
                </CardContent>
              </Card>
            )}

            {/* View Full Leaderboard Button */}
            <div className="text-center mt-6">
              <Button 
                onClick={() => navigate('/leaderboard')}
                variant="outline"
                className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
              >
                View Full Leaderboard
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* CTA Section */}
          <div className="max-w-3xl mx-auto text-center">
            <Card className="bg-gradient-to-r from-yellow-500 to-orange-500 border-0 shadow-2xl shadow-yellow-500/50">
              <CardContent className="py-12 px-6">
                <h2 className="text-3xl sm:text-4xl font-black text-black mb-4">
                  ⚡ DON'T MISS IT! ⚡
                </h2>
                <div className="space-y-3 mb-8">
                  <div className="flex items-center justify-center gap-3 text-black text-lg font-bold">
                    <Calendar className="w-6 h-6" />
                    <span>Every Sunday at 9 PM</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-black text-lg font-bold">
                    <Trophy className="w-6 h-6" />
                    <span>Winner Declared at 10 PM</span>
                  </div>
                  <div className="flex items-center justify-center gap-3 text-black text-lg font-bold">
                    <ExternalLink className="w-6 h-6" />
                    <span>fokatmain.com</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button 
                    size="lg"
                    onClick={handleCTA}
                    className="bg-black text-yellow-400 hover:bg-gray-900 font-black text-lg px-8 py-6 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                  >
                    <Play className="w-6 h-6 mr-2" />
                    PLAY NOW & WIN
                    <ArrowRight className="w-6 h-6 ml-2" />
                  </Button>
                  
                  <Button 
                    size="lg"
                    onClick={() => navigate('/leaderboard')}
                    variant="outline"
                    className="border-2 border-black text-black hover:bg-black/10 font-bold text-lg px-8 py-6"
                  >
                    <Trophy className="w-5 h-5 mr-2" />
                    Check Leaderboard
                  </Button>
                </div>

                <p className="mt-6 text-black/80 text-base font-semibold animate-pulse">
                  🔥 Play Sunday 9 PM! Winner declared at 10 PM same day! Tag friends to compete! 💪
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Footer Note */}
          <div className="text-center mt-12 text-gray-400 text-sm space-y-2">
            <p>#Fokatmain #WinnerAnnouncement #SkillGames #SundaySpecial</p>
            <p className="text-xs">100% Skill-Based • Fair Play Guaranteed • Real Prizes</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Instagram;
